// El saludo de entrada.
//
// Antes la pagina abria con la palabra "Resumen" y un titulo. Esta es la
// pantalla que mira el dueno cada manana, y lo primero que tiene que hacer es
// decirle como esta el negocio hoy, no como se llama la seccion.
//
// El saludo cambia con la hora de Lima y la linea de abajo son numeros de
// verdad: lo que va del mes y lo que sale a la calle hoy.

import { useRef, useState } from 'react';
import axios from 'axios';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';

import { useAuthContext } from '../../context/authContext';
import { fotoDePerfil } from '../../utils/sesion';
import { soles, numero, nombreMes, fechaLarga, conMayuscula } from './formato';
import { useConteo, SUAVE } from './motion';

const saludoDeLaHora = () => {
  const hora = Number(
    new Date().toLocaleString('en-US', { timeZone: 'America/Lima', hour: '2-digit', hour12: false })
  );
  /* De madrugada se saluda "buenas noches", no "buenos días": a la una de
     la mañana nadie esta empezando el dia. */
  if (hora < 5) return 'Buenas noches';
  if (hora < 12) return 'Buenos días';
  if (hora < 19) return 'Buenas tardes';
  return 'Buenas noches';
};

export default function SaludoCeo({ datos }) {
  const { planInfo, setPlanInfo, token, baseUrl } = useAuthContext();
  const selector = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const [falla, setFalla] = useState('');

  /**
   * Cambiar la foto: se elige el archivo, se sube y se ve al instante.
   *
   * El servidor devuelve la direccion de la foto ya guardada y esa se escribe
   * en la sesion del navegador. Sin eso habria que cerrar sesion y volver a
   * entrar para verla, porque el perfil se guarda al momento de ingresar.
   */
  const cambiarFoto = (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;

    setFalla('');
    setSubiendo(true);

    const cuerpo = new FormData();
    cuerpo.append('foto', archivo);

    axios.post(`${baseUrl}admin/perfil/foto`, cuerpo, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        const imagen = r.data?.data?.imagen || r.data?.imagen;
        if (!imagen) throw new Error('sin imagen');

        const actualizado = { ...planInfo, profile: { ...(planInfo?.profile || {}), image: imagen } };
        try { window.localStorage.setItem('inf', JSON.stringify(actualizado)); } catch (e) { /* modo privado */ }
        setPlanInfo(actualizado);
      })
      .catch((e) => setFalla(e?.response?.data?.message || 'No pudimos subir la foto. Que sea JPG o PNG, menos de 5 MB.'))
      .finally(() => setSubiendo(false));
  };

  /* El nombre sale del perfil que guarda el login. Si esa sesion es vieja y
     no lo trae, el saludo va solo: "Buenos días, undefined" es peor que no
     saludar por nombre. */
  const nombre = planInfo?.profile?.name || '';
  const foto = fotoDePerfil(planInfo);
  const inicial = (nombre || planInfo?.log || 'A').charAt(0).toUpperCase();
  const pedidosEnCurso = useConteo(datos.operacion.pedidosHoy, 1100);

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2.5, md: 3.5 },
        border: '1px solid',
        borderColor: 'secondary.light',
        boxShadow: 'none',
        backgroundColor: 'secondary.lighter',
        backgroundImage: `
          radial-gradient(520px 200px at 92% -30%, rgba(60,251,159,.55), transparent 70%),
          radial-gradient(420px 220px at 8% 130%, rgba(15,139,84,.18), transparent 70%),
          linear-gradient(118deg, #EAFEF3 0%, #FCFCFA 52%, #DFFBEE 100%)
        `,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2.5, md: 3 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2.5} alignItems="center" sx={{ minWidth: 0 }}>
          {/* El aro de menta late despacio alrededor de la foto: la pagina
              se siente despierta sin que nada parpadee. */}
          <Box
            sx={{
              position: 'relative',
              flexShrink: 0,
              '@keyframes aro': {
                '0%': { transform: 'scale(1)', opacity: .5 },
                '70%, 100%': { transform: 'scale(1.35)', opacity: 0 },
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -2,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'secondary.main',
                animation: `aro 3.2s ${SUAVE} infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
              },
            }}
          >
          <Tooltip title={foto ? 'Cambiar mi foto' : 'Poner mi foto'} arrow>
          <Box
            component="button"
            type="button"
            onClick={() => selector.current?.click()}
            aria-label={foto ? 'Cambiar mi foto' : 'Poner mi foto'}
            sx={{
              p: 0, border: 'none', background: 'none', cursor: 'pointer',
              position: 'relative', borderRadius: '50%', display: 'block',
              '&:hover .capaFoto, &:focus-visible .capaFoto': { opacity: 1 },
            }}
          >
          <Avatar
            src={foto || undefined}
            sx={{
              width: { xs: 56, md: 66 },
              height: { xs: 56, md: 66 },
              flexShrink: 0,
              bgcolor: 'primary.darker',
              color: 'secondary.main',
              fontSize: { xs: 22, md: 26 },
              fontWeight: 700,
              border: '3px solid',
              borderColor: 'common.white',
              boxShadow: (t) => t.customShadows.z8,
            }}
          >
            {inicial}
          </Avatar>

            <Box
              className="capaFoto"
              sx={{
                position: 'absolute', inset: 3, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'rgba(15,42,30,.55)', color: 'common.white',
                opacity: subiendo ? 1 : 0,
                transition: `opacity .25s ${SUAVE}`,
              }}
            >
              {subiendo
                ? <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
                : <PhotoCameraRoundedIcon sx={{ fontSize: 20 }} />
              }
            </Box>
          </Box>
          </Tooltip>

          <input
            ref={selector}
            type="file"
            accept="image/jpeg,image/png"
            onChange={cambiarFoto}
            style={{ display: 'none' }}
          />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 26, md: 34 },
                fontWeight: 800,
                letterSpacing: '-1px',
                lineHeight: 1.15,
                color: 'primary.darker',
              }}
            >
              {saludoDeLaHora()}{nombre ? `, ${nombre}` : ''}
            </Typography>

            <Typography sx={{ fontSize: { xs: 13.5, md: 15 }, color: 'primary.dark', mt: .25 }}>
              {conMayuscula(nombreMes(datos.hoy))} va {soles(datos.mes.monto)} y hoy
              salen {numero(datos.operacion.pedidosHoy)} pedidos.
            </Typography>

            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: .5 }}>
              {conMayuscula(fechaLarga(datos.hoy))}
            </Typography>

            {falla &&
              <Typography sx={{ fontSize: 12.5, color: 'error.dark', mt: .5 }}>{falla}</Typography>
            }
          </Box>
        </Stack>

        {/* El numero del dia, en vidrio sobre el degradado. Es la primera
            pregunta de toda la manana: cuanto sale hoy. */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 1.75,
            borderRadius: 3,
            bgcolor: 'rgba(252,252,250,.72)',
            border: '1px solid',
            borderColor: 'rgba(24,58,42,.07)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <Box
            sx={{
              width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'primary.darker', color: 'secondary.main',
            }}
          >
            <LocalShippingRoundedIcon sx={{ fontSize: 22 }} />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: 'primary.darker' }}>
              {numero(Math.round(pedidosEnCurso))}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              pedidos salen hoy
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}
